/** @jsxImportSource @emotion/react */
'use memo'

import styled from '@emotion/styled'
import colors from '@sportninja/common/constants/appColors'
import type { OrgRegistrationCardModel } from '@sportninja/common/features/organization/registration/utils/cardModel'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  captionXSmall,
  paragraphSmall,
  paragraphXSmall,
  weightSemiBold,
} from 'src/components/css'
import { COUNTDOWN_COLORS, SPOTS_COLORS } from '../../../constants/tones'

interface Props {
  model: OrgRegistrationCardModel
}

/**
 * The card's countdown in a line or two. Open: days left, colored by urgency,
 * over the spots summary. Upcoming or closed: what is counted ("Opens In",
 * "Closed On") over the value, since the column's own heading reads Closes In.
 */
const ClosesInCell = ({ model }: Props) => {
  'use memo'

  const isOpen = model.badge === 'open' || model.badge === 'closingSoon'

  return (
    <Wrapper>
      {!isOpen && <Caption>{capitalizeWords(model.countdownLabel)}</Caption>}
      <Value color={COUNTDOWN_COLORS[model.badge]}>
        {model.countdownValue}
      </Value>
      {isOpen && model.spotsLabel ? (
        <Spots color={SPOTS_COLORS[model.spots.tone]}>{model.spotsLabel}</Spots>
      ) : null}
    </Wrapper>
  )
}

export default ClosesInCell

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  height: 100%;
  line-height: 1.3;
  white-space: nowrap;
`

const Caption = styled.span`
  ${captionXSmall}
  ${weightSemiBold}
  color: ${colors.NEUTRAL_60};
`

const Value = styled.span<{ color: string }>`
  ${paragraphSmall}
  ${weightSemiBold}
  color: ${({ color }) => color};
`

const Spots = styled.span<{ color: string }>`
  ${paragraphXSmall}
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ color }) => color};
`
